/**
 * SharePromptCard — displays a single share suggestion in the producer dashboard.
 * Clicking the CTA opens Share Studio with the activity pre-loaded.
 */

import { Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { getPromptCopy } from './sharePromptsCopy';
import type { EnrichedSharePrompt } from './types';

interface SharePromptCardProps {
  prompt: EnrichedSharePrompt;
  onShare: (prompt: EnrichedSharePrompt) => void;
  onDismiss: (promptId: string) => void;
}

export function SharePromptCard({ prompt, onShare, onDismiss }: SharePromptCardProps) {
  const copy = getPromptCopy(prompt.prompt_type, prompt.entityTitle);

  return (
    <Card className="border-border/60 bg-accent/5 shadow-none relative group">
      <button
        onClick={() => onDismiss(prompt.id)}
        className="absolute top-2 right-2 p-1 rounded-md text-muted-foreground/40 hover:text-muted-foreground hover:bg-muted/40 transition-colors opacity-0 group-hover:opacity-100"
        aria-label="Stäng"
      >
        <X className="h-3.5 w-3.5" />
      </button>
      <CardContent className="p-3 space-y-2">
        <div className="flex items-start gap-2.5">
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 shrink-0 mt-0.5">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground leading-snug">{copy.headline}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{copy.description}</p>
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="w-full gap-1.5 text-xs"
          onClick={() => onShare(prompt)}
        >
          <Sparkles className="h-3 w-3" />
          {copy.ctaLabel}
        </Button>
      </CardContent>
    </Card>
  );
}
